import React, { useState, useRef, useEffect } from 'react';
import { speakChinese } from '../../utils/speechSynthesis';
import { API_URL } from '../../config';

function resolveAudioUrl(audio) {
  if (!audio) return null;
  if (audio.startsWith('http')) return audio;
  if (audio.startsWith('/')) return `${API_URL}${audio}`;
  return audio;
}

/**
 * ParagraphReading — single component covering the three sub-stages
 * read | translate | feedback.
 *
 * Per-sentence audio uses the existing `speakChinese` TTS util.
 */

const SpeakerIcon = ({ size = 16 }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.5"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M11 5L6 9H3v6h3l5 4V5zM15.5 8.5a5 5 0 010 7M19 5a9 9 0 010 14" />
  </svg>
);

const NOTE_INDICATOR = {
  good: { color: 'var(--jade)', glyph: '✓' },
  note: { color: 'var(--ink-3)', glyph: '·' },
  miss: { color: 'var(--vermillion)', glyph: '!' },
};

function ProgressHeader({ percent, eyebrow }) {
  return (
    <>
      <div className="progress-track" style={{ height: 4, borderRadius: 0 }}>
        <div
          className="progress-fill progress-fill--accent"
          style={{ width: `${percent}%` }}
        />
      </div>
      <div
        style={{
          padding: '24px 32px 0',
          maxWidth: 720,
          margin: '0 auto',
        }}
      >
        <div className="t-eyebrow">{eyebrow}</div>
      </div>
    </>
  );
}

function ReadStage({ passage, onTranslate, onLeave }) {
  const [showPinyin, setShowPinyin] = useState(false);
  const [speakingIdx, setSpeakingIdx] = useState(-1);
  const audioElRef = useRef(null);

  const stopAudio = () => {
    if (audioElRef.current) {
      audioElRef.current.pause();
      audioElRef.current = null;
    }
  };

  const handleSpeak = async (idx, sentence) => {
    if (speakingIdx === idx) return;
    stopAudio();
    setSpeakingIdx(idx);
    const recorded = resolveAudioUrl(sentence.audio);
    try {
      if (recorded) {
        const audio = new Audio(recorded);
        audioElRef.current = audio;
        audio.onended = () => setSpeakingIdx(-1);
        audio.onerror = () => setSpeakingIdx(-1);
        await audio.play();
      } else {
        await speakChinese(sentence.zh);
        setSpeakingIdx(-1);
      }
    } catch {
      setSpeakingIdx(-1);
    }
  };

  const allHints = passage.sentences.filter((s) => s.hint).map((s) => s.hint);

  return (
    <div style={{ minHeight: '100vh', background: 'var(--paper)' }}>
      <header className="ink-header">
        <button className="btn btn--ghost btn--sm" onClick={onLeave}>
          ← Passages
        </button>
        <span
          className="t-mono"
          style={{
            fontSize: 11,
            letterSpacing: '0.14em',
            textTransform: 'uppercase',
            color: 'var(--ink-3)',
          }}
        >
          Reading · 01 · Read
        </span>
        <button
          className="btn btn--ghost btn--sm"
          onClick={() => setShowPinyin((v) => !v)}
        >
          {showPinyin ? 'Hide pinyin' : 'Show pinyin'}
        </button>
      </header>

      <ProgressHeader
        percent={33}
        eyebrow={`Passage · HSK ${passage.hskLevel} · ${passage.charCount} characters`}
      />

      <div style={{ maxWidth: 720, margin: '0 auto', padding: '16px 32px 64px' }}>
        <h1
          style={{
            fontFamily: 'var(--font-han)',
            fontSize: 44,
            lineHeight: 1.1,
            letterSpacing: '0.03em',
            margin: '8px 0 4px',
            color: 'var(--ink)',
          }}
        >
          {passage.title}
        </h1>
        <p className="pinyin" style={{ fontSize: 17, marginBottom: 32 }}>
          {passage.subtitle}
        </p>

        <div className="card" style={{ padding: 28, marginBottom: 24 }}>
          {passage.sentences.map((s, i) => (
            <div
              key={i}
              style={{
                paddingBottom: i === passage.sentences.length - 1 ? 0 : 20,
                marginBottom: i === passage.sentences.length - 1 ? 0 : 20,
                borderBottom:
                  i === passage.sentences.length - 1
                    ? 'none'
                    : '1px solid var(--rule-2)',
              }}
            >
              <div
                style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 14,
                }}
              >
                <span
                  className="t-mono"
                  style={{
                    fontSize: 11,
                    letterSpacing: '0.14em',
                    color: 'var(--ink-3)',
                    paddingTop: 12,
                    minWidth: 22,
                  }}
                >
                  {String(i + 1).padStart(2, '0')}
                </span>
                <div style={{ flex: 1 }}>
                  <div
                    style={{
                      fontFamily: 'var(--font-han)',
                      fontSize: 28,
                      lineHeight: 1.7,
                      color: 'var(--ink)',
                    }}
                  >
                    {s.zh}
                  </div>
                  {showPinyin && (
                    <div
                      className="pinyin"
                      style={{
                        fontSize: 14,
                        marginTop: 4,
                        color: 'var(--ink-3)',
                      }}
                    >
                      {s.py}
                    </div>
                  )}
                </div>
                <button
                  type="button"
                  onClick={() => handleSpeak(i, s)}
                  aria-label={`Play sentence ${i + 1}`}
                  style={{
                    width: 32,
                    height: 32,
                    border: '1px solid var(--rule)',
                    borderRadius: 'var(--r2)',
                    background: speakingIdx === i ? 'var(--paper-2)' : 'var(--card)',
                    color: 'var(--ink-2)',
                    cursor: 'pointer',
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                    marginTop: 4,
                  }}
                >
                  <SpeakerIcon />
                </button>
              </div>
            </div>
          ))}
        </div>

        {allHints.length > 0 && (
          <div
            style={{
              background: 'var(--paper-2)',
              border: '1px solid var(--rule)',
              borderRadius: 'var(--r3)',
              padding: 16,
              marginBottom: 24,
            }}
          >
            <div className="t-eyebrow" style={{ marginBottom: 8 }}>
              Vocabulary hints
            </div>
            <ul style={{ margin: 0, paddingLeft: 20 }}>
              {allHints.map((h, i) => (
                <li key={i} className="t-small" style={{ marginBottom: 4 }}>
                  {h}
                </li>
              ))}
            </ul>
          </div>
        )}

        <button
          className="btn btn--accent btn--lg btn--block"
          onClick={onTranslate}
        >
          I'm ready · translate →
        </button>
      </div>
    </div>
  );
}

function TranslateStage({
  passage,
  studentTranslation,
  setStudentTranslation,
  onSubmit,
  onBackToRead,
  onLeave,
  submitting,
  submitError,
  thinking,
}) {
  const [showPinyin, setShowPinyin] = useState(false);
  const trimmed = studentTranslation.trim();
  const canSubmit = trimmed.length >= 20 && !submitting;

  return (
    <div style={{ minHeight: '100vh', background: 'var(--paper)' }}>
      <header className="ink-header">
        <button className="btn btn--ghost btn--sm" onClick={onLeave}>
          ← Passages
        </button>
        <span
          className="t-mono"
          style={{
            fontSize: 11,
            letterSpacing: '0.14em',
            textTransform: 'uppercase',
            color: 'var(--ink-3)',
          }}
        >
          Reading · 02 · Translate
        </span>
        <button className="btn btn--ghost btn--sm" onClick={onBackToRead}>
          ← Re-read
        </button>
      </header>

      <ProgressHeader percent={66} eyebrow="Translate · 翻译" />

      <div style={{ maxWidth: 720, margin: '0 auto', padding: '16px 32px 64px' }}>
        <h1 className="t-h1" style={{ fontSize: 32, marginBottom: 4 }}>
          Write what it means.
        </h1>
        <p className="t-body" style={{ marginBottom: 24 }}>
          In your own words. Aim for 2–4 English sentences.
        </p>

        <div
          style={{
            background: 'var(--paper-2)',
            border: '1px solid var(--rule)',
            borderRadius: 'var(--r3)',
            padding: 20,
            marginBottom: 24,
          }}
        >
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'baseline',
              marginBottom: 12,
            }}
          >
            <div className="t-eyebrow">Source · {passage.title}</div>
            <button
              type="button"
              onClick={() => setShowPinyin((v) => !v)}
              style={{
                background: 'transparent',
                border: 0,
                padding: 0,
                color: 'var(--ink-2)',
                cursor: 'pointer',
                fontFamily: 'var(--font-mono)',
                fontSize: 11,
                letterSpacing: '0.14em',
                textTransform: 'uppercase',
                textDecoration: 'underline',
                textUnderlineOffset: 3,
              }}
            >
              {showPinyin ? 'Hide pinyin' : 'Show pinyin'}
            </button>
          </div>

          {showPinyin ? (
            <div>
              {passage.sentences.map((s, i) => (
                <div
                  key={i}
                  style={{
                    paddingBottom: i === passage.sentences.length - 1 ? 0 : 10,
                    marginBottom: i === passage.sentences.length - 1 ? 0 : 10,
                    borderBottom:
                      i === passage.sentences.length - 1
                        ? 'none'
                        : '1px solid var(--rule-2)',
                  }}
                >
                  <div
                    style={{
                      fontFamily: 'var(--font-han)',
                      fontSize: 20,
                      lineHeight: 1.5,
                      color: 'var(--ink)',
                    }}
                  >
                    {s.zh}
                  </div>
                  <div
                    className="pinyin"
                    style={{ fontSize: 13, marginTop: 2, color: 'var(--ink-3)' }}
                  >
                    {s.py}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div
              style={{
                fontFamily: 'var(--font-han)',
                fontSize: 20,
                lineHeight: 1.6,
                color: 'var(--ink)',
              }}
            >
              {passage.sentences.map((s) => s.zh).join('')}
            </div>
          )}
        </div>

        <div className="field" style={{ marginBottom: 12 }}>
          <label className="field-label">Your translation</label>
          <textarea
            className="textarea"
            placeholder="Write what the passage means in your own words…"
            value={studentTranslation}
            onChange={(e) => setStudentTranslation(e.target.value)}
            rows={8}
            style={{ minHeight: 200, fontSize: 15, lineHeight: 1.55 }}
          />
        </div>

        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: 24,
          }}
        >
          <span className="t-small">Aim for 2–4 sentences.</span>
          <span className="t-small t-mono">
            {trimmed.length} chars{trimmed.length < 20 && ' · 20 to submit'}
          </span>
        </div>

        {submitError && (
          <p style={{ color: 'var(--error)', fontSize: 13, marginBottom: 12 }}>
            {submitError}
          </p>
        )}

        <div style={{ display: 'flex', gap: 12 }}>
          <button
            className="btn btn--ghost"
            style={{ flex: 1 }}
            onClick={onBackToRead}
            disabled={submitting}
          >
            ← Re-read passage
          </button>
          <button
            className="btn btn--accent"
            style={{ flex: 2 }}
            disabled={!canSubmit}
            onClick={onSubmit}
          >
            {submitting ? 'Grading…' : 'Submit for feedback →'}
          </button>
        </div>

        {submitting && <GradingHeartbeat thinking={thinking} />}
      </div>
    </div>
  );
}

function FeedbackStage({
  passage,
  studentTranslation,
  feedback,
  onTryAnother,
  onRevise,
  onLeave,
}) {
  return (
    <div style={{ minHeight: '100vh', background: 'var(--paper)' }}>
      <header className="ink-header">
        <button className="btn btn--ghost btn--sm" onClick={onLeave}>
          ← Passages
        </button>
        <span
          className="t-mono"
          style={{
            fontSize: 11,
            letterSpacing: '0.14em',
            textTransform: 'uppercase',
            color: 'var(--ink-3)',
          }}
        >
          Reading · 03 · Feedback
        </span>
        <div style={{ width: 80 }} />
      </header>

      <ProgressHeader percent={100} eyebrow="Feedback · 反馈" />

      <div style={{ maxWidth: 880, margin: '0 auto', padding: '16px 32px 64px' }}>
        {/* Score header */}
        <div
          style={{
            display: 'flex',
            alignItems: 'baseline',
            gap: 24,
            marginBottom: 8,
          }}
        >
          <div
            className="t-display"
            style={{
              fontSize: 96,
              lineHeight: 1,
              fontWeight: 500,
              letterSpacing: '-0.02em',
            }}
          >
            {feedback.score}
            <span
              style={{
                fontSize: 32,
                color: 'var(--ink-3)',
                marginLeft: 8,
                fontWeight: 400,
              }}
            >
              / 100
            </span>
          </div>
          <span className="badge badge--jade" style={{ fontSize: 12 }}>
            {feedback.scoreLabel}
          </span>
        </div>
        <p className="t-body" style={{ marginBottom: 32 }}>
          Here's how your translation compares.
        </p>

        {/* Side-by-side */}
        <div
          className="pr-feedback-grid"
          style={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gap: 16,
            marginBottom: 24,
          }}
        >
          <div className="card" style={{ padding: 20 }}>
            <div className="t-eyebrow" style={{ marginBottom: 12 }}>
              Source · 原文
            </div>
            <div
              style={{
                fontFamily: 'var(--font-han)',
                fontSize: 18,
                lineHeight: 1.6,
                color: 'var(--ink)',
              }}
            >
              {passage.sentences.map((s) => s.zh).join('')}
            </div>
          </div>
          <div className="card" style={{ padding: 20 }}>
            <div className="t-eyebrow" style={{ marginBottom: 12 }}>
              Your translation
            </div>
            <div
              style={{
                fontSize: 15,
                lineHeight: 1.6,
                color: 'var(--ink)',
                whiteSpace: 'pre-wrap',
              }}
            >
              {studentTranslation}
            </div>
          </div>
        </div>

        {/* Notes from the AI tutor */}
        <div className="card" style={{ padding: 24, marginBottom: 24 }}>
          <h2 className="t-h3" style={{ marginBottom: 16 }}>
            Notes from the AI tutor
          </h2>
          <ul style={{ margin: 0, padding: 0, listStyle: 'none' }}>
            {feedback.notes.map((n, i) => {
              const ind = NOTE_INDICATOR[n.type] || NOTE_INDICATOR.note;
              return (
                <li
                  key={i}
                  style={{
                    display: 'flex',
                    gap: 14,
                    paddingBottom: 14,
                    marginBottom: 14,
                    borderBottom:
                      i === feedback.notes.length - 1
                        ? 'none'
                        : '1px solid var(--rule-2)',
                  }}
                >
                  <span
                    style={{
                      width: 24,
                      height: 24,
                      borderRadius: 999,
                      border: `1.5px solid ${ind.color}`,
                      color: ind.color,
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: 13,
                      fontWeight: 600,
                      flexShrink: 0,
                    }}
                  >
                    {ind.glyph}
                  </span>
                  <p style={{ margin: 0, fontSize: 14, lineHeight: 1.55, color: 'var(--ink-2)' }}>
                    {n.text}
                  </p>
                </li>
              );
            })}
          </ul>
        </div>

        {/* Reference translation */}
        <div
          style={{
            background: 'var(--paper-2)',
            border: '1px solid var(--rule)',
            borderRadius: 'var(--r3)',
            padding: 20,
            marginBottom: 32,
          }}
        >
          <div className="t-eyebrow" style={{ marginBottom: 8 }}>
            Reference translation
          </div>
          <p
            style={{
              margin: 0,
              fontStyle: 'italic',
              color: 'var(--ink-2)',
              fontSize: 15,
              lineHeight: 1.6,
            }}
          >
            {feedback.referenceTranslation}
          </p>
        </div>

        <div style={{ display: 'flex', gap: 12 }}>
          <button className="btn btn--ghost" style={{ flex: 1 }} onClick={onTryAnother}>
            Try another passage
          </button>
          <button className="btn" style={{ flex: 1 }} onClick={onRevise}>
            Revise my translation
          </button>
        </div>
      </div>
    </div>
  );
}

// Generic high-level phases shown while the AI grades. Intentionally vague —
// they avoid leaking the rubric ("90-100 = excellent…"), exact scores, or any
// phrasing the student could game.
const GRADING_PHASES = [
  'Settling in…',
  'Reading the source passage…',
  'Reading your translation…',
  'Comparing meaning…',
  'Considering tone and nuance…',
  'Drafting tutor notes…',
];

const PHASE_INTERVAL_MS = 3500;

/**
 * GradingHeartbeat — a tiny inline indicator (pulsing dot + rotating phrase)
 * shown next to/under the submit button while grading is in flight. We do
 * NOT show the raw model thinking; just rotate generic phase labels.
 */
function GradingHeartbeat({ thinking }) {
  const [phaseIdx, setPhaseIdx] = useState(0);

  useEffect(() => {
    const id = setInterval(() => {
      setPhaseIdx((i) => Math.min(i + 1, GRADING_PHASES.length - 1));
    }, PHASE_INTERVAL_MS);
    return () => clearInterval(id);
  }, []);

  // Skip past "Settling in…" once tokens start arriving.
  useEffect(() => {
    if (thinking && thinking.length > 0) {
      setPhaseIdx((i) => (i === 0 ? 1 : i));
    }
  }, [thinking]);

  const label = GRADING_PHASES[phaseIdx];

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        marginTop: 14,
        justifyContent: 'center',
      }}
    >
      <span
        aria-hidden
        style={{
          display: 'inline-block',
          width: 8,
          height: 8,
          borderRadius: '50%',
          background: 'var(--ink-2)',
          animation: 'inkPulse 1.4s ease-in-out infinite',
        }}
      />
      <span
        key={label}
        className="grading-phase"
        style={{
          fontFamily: 'var(--font-mono)',
          fontSize: 12,
          letterSpacing: '0.06em',
          color: 'var(--ink-3)',
        }}
      >
        {label}
      </span>
      <style>{`
        @keyframes inkPulse {
          0%, 100% { opacity: 0.3; transform: scale(0.9); }
          50%      { opacity: 1;   transform: scale(1.15); }
        }
        .grading-phase {
          animation: phaseFade 500ms ease-out both;
        }
        @keyframes phaseFade {
          from { opacity: 0; transform: translateY(3px); }
          to   { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </div>
  );
}

function ParagraphReading({
  stage,
  passage,
  studentTranslation,
  setStudentTranslation,
  feedback,
  submitting,
  submitError,
  thinking,
  onTranslate,
  onSubmit,
  onRevise,
  onBackToRead,
  onTryAnother,
  onLeave,
}) {
  if (stage === 'read') {
    return <ReadStage passage={passage} onTranslate={onTranslate} onLeave={onLeave} />;
  }
  if (stage === 'translate') {
    return (
      <TranslateStage
        passage={passage}
        studentTranslation={studentTranslation}
        setStudentTranslation={setStudentTranslation}
        onSubmit={onSubmit}
        onBackToRead={onBackToRead}
        onLeave={onLeave}
        submitting={submitting}
        submitError={submitError}
        thinking={thinking}
      />
    );
  }
  return (
    <FeedbackStage
      passage={passage}
      studentTranslation={studentTranslation}
      feedback={feedback}
      onTryAnother={onTryAnother}
      onRevise={onRevise}
      onLeave={onLeave}
    />
  );
}

export default ParagraphReading;
